// @ts-nocheck
/*
 * Port of views/dashboard/campaigns/list.js (1281 lines).
 * BYTE-IDENTICAL — Live Campaigns list. PrimeReact DataTable/Column swapped
 * for obd3-gui DataTable + tanstack ColumnDef. PrimeReact Button swapped for
 * shadcn Button. Take-Action Dropdown kept as react-bootstrap. All status
 * transitions, KPI pill sections, date filter, filtered CSV export, and the
 * refresh-without-blank pattern preserved verbatim.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/data-table/DataTable';
import { useNavigate } from 'react-router-dom';
import { ROUTES, USER_ROLES, MODULE_ACL } from '@/utilities/constant';
import { canCreateCampaign, canDownloadReports } from '@/utilities/promptUploadAccess';
import { exportToCSV, formatErrorDesc, hasPermission } from '@/utilities/helpers';
import { getUser, Storage } from '@/utilities/setting';
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_VOICE_CAMPAIGNS_LIVE,
  API_VOICE_CAMPAIGNS_UPDATE,
  DOWNLOAD_REPORT_CAMPAIGN,
} from '@/service/url';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import Loader from '@/components/partials/components/Loader';
import { toast } from 'sonner';
import {
  FiCheckCircle,
  FiClock,
  FiList,
  FiBarChart2,
  FiPhone,
  FiSlash,
  FiRefreshCw,
  FiEye,
} from 'react-icons/fi';
import {
  Eye,
  Copy,
  Pause,
  Play,
  Square,
  XCircle,
  CheckCircle,
  BarChart3,
  FileText as FileIcon,
  Link2,
} from 'lucide-react';
import NumbersViewerModal from './NumbersViewerModal';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Download, Plus, RefreshCw } from 'lucide-react';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';

const getTodayStr = () => new Date().toLocaleDateString('en-CA');
const getFirstOfMonthStr = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toLocaleDateString('en-CA');
};
const getSixMonthsAgoStr = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 6);
  return d.toLocaleDateString('en-CA');
};

const CampaignsList = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState<any>(null);
  const [showCreateCampaigns, setShowCreateCampaigns] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loggedUserRole, setLoggedUserRole] = useState('');
  const [loggedUserId, setLoggedUserId] = useState<any>(null);
  const [fromDate, setFromDate] = useState(getFirstOfMonthStr());
  const [toDate, setToDate] = useState(getTodayStr());
  const [appliedDates, setAppliedDates] = useState({
    start: getFirstOfMonthStr(),
    end: getTodayStr(),
  });

  const buildDate = (date, isEnd = false) => {
    if (!date) return null;
    const [y, m, d] = date.split('-');
    return new Date(
      y,
      m - 1,
      d,
      isEnd ? 23 : 0,
      isEnd ? 59 : 0,
      isEnd ? 59 : 0,
      isEnd ? 999 : 0,
    ).getTime();
  };

  const fetchCampaigns = async (
    role = loggedUserRole,
    agentId = loggedUserId,
    start = fromDate,
    end = toDate,
  ) => {
    setLoading(true);
    let url = API_VOICE_CAMPAIGNS_LIVE;
    const queryParams = new URLSearchParams();
    if (role === USER_ROLES.CALL_CENTRE_AGENT && agentId) {
      queryParams.append('agent_id', agentId);
    }
    if (start) queryParams.append('start_date', start);
    if (end) queryParams.append('end_date', end);
    const finalUrl = `${url}?${queryParams.toString()}`;
    try {
      const res = await getApiCall(finalUrl, {});
      onCampaignListSuccess(res);
    } catch (error) {
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  // Realtime refetch — does NOT blank the page (uses `refreshing`, not `loading`)
  const handleRefresh = async () => {
    setRefreshing(true);
    let url = API_VOICE_CAMPAIGNS_LIVE;
    const queryParams = new URLSearchParams();
    if (loggedUserRole === USER_ROLES.CALL_CENTRE_AGENT && loggedUserId) {
      queryParams.append('agent_id', loggedUserId);
    }
    if (appliedDates.start) queryParams.append('start_date', appliedDates.start);
    if (appliedDates.end) queryParams.append('end_date', appliedDates.end);
    try {
      const res = await getApiCall(`${url}?${queryParams.toString()}`, {});
      onCampaignListSuccess(res);
    } catch (error) {
      onError(error);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    const fetchUserDataAndCampaigns = async () => {
      const rawUser = Storage.getItem('user');
      if (!rawUser) {
        console.error('User data not found in Storage');
        return;
      }
      let user;
      try {
        user = JSON.parse(rawUser);
      } catch (e) {
        console.error('Invalid JSON in Storage user:', e);
        return;
      }
      const role = user?.role;
      setLoggedUserRole(role);
      if (role === USER_ROLES.CALL_CENTRE_ADMIN) {
        setShowCreateCampaigns(true);
      } else {
        setShowCreateCampaigns(false);
      }
      if (role === USER_ROLES.CALL_CENTRE_AGENT) {
        const id = user?.agentData?.id;
        setLoggedUserId(id);
        fetchCampaigns(role, id);
      } else {
        fetchCampaigns(role);
      }
    };
    fetchUserDataAndCampaigns();
  }, []);

  function onCampaignListSuccess(resp) {
    setCampaigns(resp);
  }

  function onError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] =
    useState<any>({});

  useEffect(() => {
    const user = getUser();
    if (user) {
      if (user?.acl_settings) {
        setModuleOperationsForReadPermissionCheck(parseModuleOperations(user.acl_settings.module_operations));
      }
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const handleExport = async () => {
    try {
      setButtonDisable(true);
      exportToCSV({
        fileName: 'outbound-campaigns',
        data: filteredCampaigns,
        columns: [
          { header: 'Outbound Campaign', accessor: (row) => row.campaign_name || '-' },
          {
            header: 'Created On',
            accessor: (row) =>
              new Date(row.created_at).toLocaleString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              }),
          },
          { header: 'Username', accessor: (row) => row.user?.username || '-' },
          {
            header: 'Start Date',
            accessor: (row) => formatDateTime(row.start_date, row.start_hours).full || '-',
          },
          {
            header: 'End Date',
            accessor: (row) => formatDateTime(row.end_date, row.end_hours).full || '-',
          },
          {
            header: 'Template',
            accessor: (row) => {
              const templateMap = { '0': 'Simple IVR', '1': 'DTMF', '2': 'Call Patch', '3': 'Custom IVR' };
              return templateMap[row.template_id] || 'N/A';
            },
          },
          {
            header: 'Dialer Type',
            accessor: (row) => {
              const typeMap = { '1': 'Click2Call', '2': 'Auto Dialer', '3': 'Predictive Dialer', '4': 'Power Dialer' };
              return typeMap[row.campaign_type] || 'N/A';
            },
          },
          { header: 'Total Numbers', accessor: (row) => row.total_nos || 0 },
          { header: 'Completed', accessor: (row) => row.nos_processed || 0 },
          {
            header: 'Remaining',
            accessor: (row) =>
              row.total_nos ? Number(row.total_nos) - Number(row.nos_processed || 0) : 0,
          },
          { header: 'Status', accessor: (row) => row.campaign_status || '-' },
          { header: 'Error Description', accessor: (row) => formatErrorDesc(row.error_desc) },
        ],
      });
    } finally {
      setButtonDisable(false);
    }
  };

  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [numbersViewer, setNumbersViewer] = useState({ show: false, id: null, name: null, total: 0 } as any);

  const handleCreateCampaign = () => {
    navigate(`/${ROUTES.CAMPAIGNS.CREATE}`);
  };

  // Live Campaigns list already excludes Error(4)/Error(5)/Cancelled(7) server-side.
  // Name search is handled by the DataTable's built-in globalFilter — this
  // memo only owns the date-range and status-code filters.
  const filteredCampaigns = campaigns.filter((campaign) => {
    const start = buildDate(appliedDates.start);
    const end = buildDate(appliedDates.end, true);
    const createdAtTs = campaign?.created_at ? new Date(campaign.created_at).getTime() : null;
    if (start && createdAtTs && createdAtTs < start) return false;
    if (end && createdAtTs && createdAtTs > end) return false;
    if (statusFilter !== '' && Number(campaign.campaign_status) !== Number(statusFilter)) {
      return false;
    }
    return true;
  });

  // Tone-tinted status pill matching the pill vocabulary used elsewhere
  // (Trunks / Country Codes / Licence History). Each row is:
  //   [tailwind classes for the pill fill/ring/text, human label, live?]
  // Live statuses get a pulsing dot on the left so a glance at the table
  // shows which campaigns are actively moving.
  const statusBodyTemplate = (rowData) => {
    const statusMap: Record<number, { label: string; tone: string; live?: boolean }> = {
      0:  { label: 'Ready',           tone: 'bg-sky-100 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300' },
      1:  { label: 'Running',         tone: 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300', live: true },
      2:  { label: 'Completed',       tone: 'bg-indigo-100 text-indigo-700 ring-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300' },
      3:  { label: 'Low Balance',     tone: 'bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300' },
      4:  { label: 'Error',           tone: 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300' },
      5:  { label: 'Error',           tone: 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300' },
      6:  { label: 'Cancelling',      tone: 'bg-orange-100 text-orange-700 ring-orange-200 dark:bg-orange-950/40 dark:text-orange-300' },
      7:  { label: 'Cancelled',       tone: 'bg-pink-100 text-pink-700 ring-pink-200 dark:bg-pink-950/40 dark:text-pink-300' },
      8:  { label: 'Pausing',         tone: 'bg-cyan-100 text-cyan-700 ring-cyan-200 dark:bg-cyan-950/40 dark:text-cyan-300' },
      9:  { label: 'Paused',          tone: 'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800/60 dark:text-slate-300' },
      10: { label: 'Resuming',        tone: 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300', live: true },
      21: { label: 'DND Processing',  tone: 'bg-orange-100 text-orange-700 ring-orange-200 dark:bg-orange-950/40 dark:text-orange-300', live: true },
      22: { label: 'Waiting for Ch.', tone: 'bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300', live: true },
    };
    const status = statusMap[rowData.campaign_status];
    if (!status) return <span className="text-muted-foreground">—</span>;
    return (
      <span
        className={
          'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
          status.tone
        }
      >
        {status.live && <span className="live-status-dot" aria-hidden="true" />}
        {status.label}
      </span>
    );
  };

  const actionBodyTemplate = (rowData) => {
    const status = Number(rowData?.campaign_status);
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm">Take Action</Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onClick={() => handleAction('view', rowData.campaign_id)}>
            <Eye className="h-4 w-4" /> View
          </DropdownMenuItem>
          {canAdd && (
            <DropdownMenuItem onClick={() => handleAction('duplicate', rowData.campaign_id)}>
              <Copy className="h-4 w-4" /> Clone Campaign
            </DropdownMenuItem>
          )}
          {canEdit && status === 1 && (
            <DropdownMenuItem onClick={() => handleAction('pause', rowData.campaign_id)}>
              <Pause className="h-4 w-4" /> Pause Campaign
            </DropdownMenuItem>
          )}
          {canEdit && (status === 3 || status === 9) && (
            <DropdownMenuItem onClick={() => handleAction('resume', rowData.campaign_id)}>
              <Play className="h-4 w-4" /> Resume Campaign
            </DropdownMenuItem>
          )}
          {canEdit && status === 1 && (
            <DropdownMenuItem onClick={() => handleAction('stop', rowData.campaign_id)}>
              <Square className="h-4 w-4" /> Stop Campaign
            </DropdownMenuItem>
          )}
          {canEdit && (status === 3 || status === 4 || status === 5) && (
            <DropdownMenuItem
              onClick={() => handleAction('cancel', rowData.campaign_id)}
              className="text-destructive focus:text-destructive"
            >
              <XCircle className="h-4 w-4" /> Cancel Campaign
            </DropdownMenuItem>
          )}
          {canEdit && (status === 3 || status === 4 || status === 5) && (
            <DropdownMenuItem onClick={() => handleAction('complete', rowData.campaign_id)}>
              <CheckCircle className="h-4 w-4" /> Complete Campaign
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={() =>
              navigate(`/${ROUTES.CAMPAIGNS.REPORT.replace(':id', rowData.campaign_id)}`)
            }
          >
            <BarChart3 className="h-4 w-4" /> Detailed Report
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleGenerateReport(rowData)}>
            <FileIcon className="h-4 w-4" /> Generate Full Report
          </DropdownMenuItem>
          {Number(rowData?.webhook_count) > 0 && (
            <DropdownMenuItem onClick={() => handleGenerateWebhookReport(rowData)}>
              <Link2 className="h-4 w-4" /> Generate Webhook Report
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const handleGenerateReport = (rowData) => {
    let userId: any = null;
    try {
      userId = JSON.parse(Storage.getItem('user'))?.id;
    } catch (e) {
      userId = null;
    }
    postApiCall(DOWNLOAD_REPORT_CAMPAIGN, {
      campaign_id: rowData.campaign_id,
      campaign_name: rowData.campaign_name,
      user_id: userId,
    })
      .then(() => {
        toast.success('Report generation in-progress. Check Download Reports.');
        navigate(`/${ROUTES.IVR.DOWNLOAD_REPORT}`);
      })
      .catch(onError);
  };

  const handleGenerateWebhookReport = (rowData) => {
    let userId: any = null;
    try {
      userId = JSON.parse(Storage.getItem('user'))?.id;
    } catch (e) {
      userId = null;
    }
    postApiCall(DOWNLOAD_REPORT_CAMPAIGN, {
      campaign_id: rowData.campaign_id,
      campaign_name: rowData.campaign_name,
      user_id: userId,
      report_type: 'campaign_webhook',
    })
      .then(() => {
        toast.success('Webhook report generation in-progress. Check Download Reports.');
        navigate(`/${ROUTES.IVR.DOWNLOAD_REPORT}`);
      })
      .catch(onError);
  };

  const handleAction = (action, id) => {
    if (action === 'view') {
      const url = ROUTES.CAMPAIGNS.DETAIL.replace(':id', id);
      navigate(`/${url}`);
    }
    if (action === 'pause') {
      setLoading(true);
      const url = API_VOICE_CAMPAIGNS_UPDATE.replace(':id', id);
      postApiCall(url, { campaign_status: 8 })
        .then(onUpdateSucces)
        .catch(onError)
        .finally(() => setLoading(false));
    }
    if (action === 'resume') {
      setLoading(true);
      const url = API_VOICE_CAMPAIGNS_UPDATE.replace(':id', id);
      postApiCall(url, { campaign_status: 10 })
        .then(onUpdateSucces)
        .catch(onError)
        .finally(() => setLoading(false));
    }
    if (action === 'stop') {
      setLoading(true);
      const url = API_VOICE_CAMPAIGNS_UPDATE.replace(':id', id);
      postApiCall(url, { campaign_status: 6 })
        .then(onUpdateSucces)
        .catch(onError)
        .finally(() => setLoading(false));
    }
    if (action === 'cancel') {
      setLoading(true);
      const url = API_VOICE_CAMPAIGNS_UPDATE.replace(':id', id);
      postApiCall(url, { campaign_status: 7 })
        .then(onUpdateSucces)
        .catch(onError)
        .finally(() => setLoading(false));
    }
    if (action === 'complete') {
      setLoading(true);
      const url = API_VOICE_CAMPAIGNS_UPDATE.replace(':id', id);
      postApiCall(url, { campaign_status: 2 })
        .then(onUpdateSucces)
        .catch(onError)
        .finally(() => setLoading(false));
    }
    if (action === 'delete') {
      setLoading(true);
      const url = API_VOICE_CAMPAIGNS_UPDATE.replace(':id', id);
      postApiCall(url, { is_deleted: 1 })
        .then(onDeleteSuccess)
        .catch(onError)
        .finally(() => setLoading(false));
    }
    if (action === 'duplicate') {
      const url = ROUTES.CAMPAIGNS.DUPLICATE.replace(':duplicate_camp_id', id);
      navigate(`/${url}`);
    }
    console.log(`Action: ${action} on campaign ${id}`);
  };

  async function onUpdateSucces() {
    toast.success('Outbound Campaign Status Updated');
    let url = API_VOICE_CAMPAIGNS_LIVE;
    if (loggedUserRole === USER_ROLES.CALL_CENTRE_AGENT && loggedUserId) {
      url += `?agent_id=${loggedUserId}`;
    }
    try {
      const res = await getApiCall(url, {});
      onCampaignListSuccess(res);
    } catch (error) {
      onError(error);
    }
  }

  async function onDeleteSuccess() {
    toast.success('Outbound Campaign Deleted successfully');
    let url = API_VOICE_CAMPAIGNS_LIVE;
    if (loggedUserRole === USER_ROLES.CALL_CENTRE_AGENT && loggedUserId) {
      url += `?agent_id=${loggedUserId}`;
    }
    try {
      const res = await getApiCall(url, {});
      onCampaignListSuccess(res);
    } catch (error) {
      onError(error);
    }
  }

  const stats = useMemo(() => {
    let total = 0;
    let completed = 0;
    let running = 0;
    let paused = 0;
    let waiting = 0;
    let connected = 0;
    let retry = 0;
    let pending = 0;
    let ready = 0;

    filteredCampaigns.forEach((c) => {
      const totalNos = Number(c.total_nos || 0);
      const done = Number(c.nos_processed || 0);
      const status = Number(c.campaign_status);
      const connectedNumbers = Number(c.connected_calls || 0);
      const retries = Number(c.max_retries || 0);
      const dndCount = Number(c.dnd_count || 0);
      const autoRetryCount = Number(c.auto_retry_count || 0);

      total += totalNos;
      completed += done;
      connected += connectedNumbers;
      retry += autoRetryCount;
      pending += totalNos - dndCount - done;
      if (status === 0) ready += Number(c.total_nos || 0);

      if (status === 1 || status === 10) running += 1;
      else if (status === 8 || status === 9) paused += 1;
      else if (status === 0 || status === 21 || status === 22) waiting += 1;
    });

    const remaining = total - completed;
    const progress = total > 0 ? Math.round((completed / total) * 100) : 0;

    return {
      campaigns: filteredCampaigns.length,
      total,
      completed,
      dialed: completed,
      retry,
      tcd: completed + retry,
      pending,
      remaining,
      progress,
      running,
      paused,
      waiting,
      connected,
      ready,
    };
  }, [filteredCampaigns]);

  const formatDateTime = (dateString, timeString) => {
    if (!dateString || !timeString) {
      return { full: 'Invalid Date', compact: 'Invalid Date' };
    }
    const dateTimeStr = `${dateString}T${timeString}`;
    const date = new Date(dateTimeStr);
    if (isNaN(date.getTime())) {
      return { full: 'Invalid Date', compact: 'Invalid Date' };
    }
    const formattedFull = date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
    const formattedCompact = `${date.toISOString().slice(0, 10)} - ${timeString}`;
    return { full: formattedFull, compact: formattedCompact };
  };

  const canRead = hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.CAMPAIGNS, 'read');
  const canEdit = hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.CAMPAIGNS, 'edit');
  const canDelete = hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.CAMPAIGNS, 'delete');
  const canAdd =
    canCreateCampaign() &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.CAMPAIGNS, 'add');

  if (!canRead) {
    return (
      <div
        className="text-center p-4"
        style={{ marginTop: '50px', marginBottom: '50px', fontSize: '16px' }}
      >
        No read permission found in assigned ACL
      </div>
    );
  }

  // Don't blank the page on refetch — only show the full-page loader
  // when there's genuinely nothing to display yet.
  if (loading && campaigns.length === 0) {
    return (
      <FillPage>
        <Loader />
      </FillPage>
    );
  }

  const columns: ColumnDef<any>[] = [
    {
      id: 'campaign_name',
      accessorKey: 'campaign_name',
      header: 'Outbound Campaign',
      cell: ({ row }) => (
        <button
          type="button"
          className="text-left font-medium text-primary underline-offset-2 hover:underline"
          onClick={() =>
            navigate(`/${ROUTES.CAMPAIGNS.DETAIL.replace(':id', row.original.campaign_id)}`)
          }
        >
          {row.original.campaign_name}
        </button>
      ),
    },
    {
      accessorKey: 'created_at',
      header: 'Created on',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
          {new Date(row.original.created_at).toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
      ),
    },
    {
      accessorFn: (row) => row.user?.username,
      id: 'username',
      header: 'Username',
      cell: ({ row }) => (
        <span className="text-sm text-foreground">{row.original.user?.username || '—'}</span>
      ),
    },
    {
      accessorKey: 'start_date',
      header: 'Start Date',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
          {formatDateTime(row.original.start_date, row.original.start_hours).full}
        </span>
      ),
    },
    {
      accessorKey: 'end_date',
      header: 'End Date',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
          {formatDateTime(row.original.end_date, row.original.end_hours).full}
        </span>
      ),
    },
    {
      accessorKey: 'template_id',
      header: 'Template',
      cell: ({ row }) => {
        const templateMap = { '0': 'Simple IVR', '1': 'DTMF', '2': 'Call Patch', '3': 'Custom IVR' };
        return templateMap[row.original.template_id] || 'N/A';
      },
    },
    {
      accessorKey: 'campaign_type',
      header: 'Dialer Type',
      cell: ({ row }) => {
        const typeMap = { '1': 'Click2Call', '2': 'Auto Dialer', '3': 'Predictive Dialer', '4': 'Power Dialer' };
        return typeMap[row.original.campaign_type] || 'N/A';
      },
    },
    {
      accessorKey: 'total_nos',
      header: 'Total Numbers',
      cell: ({ row }) => (
        <span className="inline-flex items-center gap-2 tabular-nums">
          <span className="font-semibold text-foreground">{row.original.total_nos || 0}</span>
          <button
            type="button"
            title="View numbers"
            onClick={() =>
              setNumbersViewer({
                show: true,
                id: row.original.campaign_id,
                name: row.original.campaign_name,
                total: Number(row.original.total_nos) || 0,
              })
            }
            className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-primary/10 text-primary transition-colors hover:bg-primary/15"
          >
            <Eye className="h-3.5 w-3.5" />
          </button>
        </span>
      ),
    },
    {
      accessorKey: 'nos_processed',
      header: 'Completed',
      cell: ({ row }) => (
        <span className="font-semibold tabular-nums text-emerald-700 dark:text-emerald-400">
          {row.original.nos_processed || 0}
        </span>
      ),
    },
    {
      id: 'remaining',
      header: 'Remaining',
      cell: ({ row }) => {
        const remaining = row.original.total_nos
          ? Math.max(
              0,
              Number(row.original.total_nos) - Number(row.original.nos_processed || 0),
            )
          : 0;
        return (
          <span className="font-semibold tabular-nums text-amber-700 dark:text-amber-400">
            {remaining}
          </span>
        );
      },
    },
    { accessorKey: 'connected_calls', header: 'Connected Calls', cell: ({ row }) => row.original.connected_calls ?? 0 },
    // { accessorKey: 'pulses', header: 'Total Pulses', cell: ({ row }) => row.original.pulses ?? 0 },
    { accessorKey: 'dnd_count', header: 'DnD Count', cell: ({ row }) => row.original.dnd_count ?? 0 },
    // { accessorKey: 'dtmf_count', header: 'Dtmf Count', cell: ({ row }) => row.original.dtmf_count ?? 0 },
    // { accessorKey: 'dtmf1_count', header: 'Dtmf1 Count', cell: ({ row }) => row.original.dtmf1_count ?? 0 },
    // { accessorKey: 'dtmf2_count', header: 'Dtmf2 Count', cell: ({ row }) => row.original.dtmf2_count ?? 0 },
    { accessorKey: 'webhook_failed_count', header: 'Webhook Failed Count', cell: ({ row }) => row.original.webhook_failed_count ?? 0 },
    { accessorKey: 'sms_count', header: 'SMS Count', cell: ({ row }) => row.original.sms_count ?? 0 },
    // { accessorKey: 'total_variable_count', header: 'Total Variable Count', cell: ({ row }) => row.original.total_variable_count ?? 0 },
    { accessorKey: 'campaign_status', header: 'Status', cell: ({ row }) => statusBodyTemplate(row.original) },
    {
      id: 'error_desc',
      accessorFn: (row) => formatErrorDesc(row.error_desc),
      header: 'Error Description',
      cell: ({ getValue }) => (
        <span className="block max-w-[260px] truncate text-xs" title={getValue()}>{getValue()}</span>
      ),
    },
    { id: 'actions', header: 'Actions', enableSorting: false, cell: ({ row }) => actionBodyTemplate(row.original) },
    { accessorKey: 'max_retries', header: 'Retry', cell: ({ row }) => row.original.max_retries ?? 0 },
  ];

  return (
    <FillPage>
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <span className="live-dot" />
            Live Campaigns
            <span className="inline-flex rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
              {stats.campaigns} active
            </span>
          </span>
        }
        description="Realtime view of currently-running voice campaigns."
        actions={
          <>
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              disabled={refreshing}
              title="Refresh (realtime)"
              aria-label="Refresh"
            >
              <RefreshCw className={refreshing ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            </Button>
            {canAdd && (
              <Button onClick={() => navigate('/create-campaign')}>
                <Plus className="h-4 w-4" />
                New Campaign
              </Button>
            )}
            {canDownloadReports() && campaigns.length > 0 && (
              <Button variant="outline" onClick={handleExport} disabled={buttonDisable}>
                <Download className="h-4 w-4" />
                Export
              </Button>
            )}
          </>
        }
      />
      <div className="campaigns-container">
        <div className="row">
          <div className="col-12">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-0">

                {/* ── Live metric pills ──────── */}
                <div className="live-metrics-wrap mb-3">
                  <div className="metric-section">
                    <div className="metric-section__title">Overview</div>
                    <div className="flex flex-wrap gap-2">
                      <span className="metric-pill metric-pill--teal">
                        <FiBarChart2 /> Total Campaign: {stats.campaigns}
                      </span>
                      <span className="metric-pill metric-pill--teal">
                        <FiBarChart2 /> Total Ready: {stats.ready}
                      </span>
                      <span className="metric-pill metric-pill--blue">
                        <FiList /> Total Numbers: {stats.total}
                      </span>
                      <span className="metric-pill metric-pill--green">
                        <FiPhone /> Dialed: {stats.dialed}
                      </span>
                      <span className="metric-pill metric-pill--purple">
                        <FiClock /> Pending: {stats.pending}
                      </span>
                      <span className="metric-pill metric-pill--red">
                        <FiSlash /> DnD: 0
                      </span>
                      <span className="metric-pill metric-pill--green">
                        <FiCheckCircle /> Connected: {stats.connected}
                      </span>
                      <span className="metric-pill metric-pill--orange">
                        <FiBarChart2 /> Total Calls Dialed: {stats.tcd}
                      </span>
                      <span className="metric-pill metric-pill--violet">
                        <FiRefreshCw /> Retry Count: {stats.retry}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mb-4 flex flex-wrap items-end gap-3">
                  <div>
                    <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      From
                    </label>
                    <CalendarDateInput
                      value={fromDate}
                      onChange={(e) => setFromDate(e)}
                      min={getSixMonthsAgoStr()}
                      max={getTodayStr()}
                      className="form-control"
                      style={{ height: '40px', width: '170px' }}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      To
                    </label>
                    <CalendarDateInput
                      value={toDate}
                      onChange={(e) => setToDate(e)}
                      min={fromDate}
                      max={getTodayStr()}
                      className="form-control"
                      style={{ height: '40px', width: '170px' }}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      Status
                    </label>
                    <select
                      className="form-control form-select"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      style={{ height: '40px', width: '190px' }}
                    >
                      <option value="">All statuses</option>
                      <option value="0">Ready</option>
                      <option value="1">Running</option>
                      <option value="3">Low Balance</option>
                      <option value="4">Error</option>
                      <option value="5">Error</option>
                      <option value="6">Cancelling</option>
                      <option value="7">Cancelled</option>
                      <option value="8">Pausing</option>
                      <option value="9">Paused</option>
                      <option value="10">Resuming</option>
                      <option value="21">DND Processing</option>
                      <option value="22">Waiting for Channel</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => {
                        fetchCampaigns(loggedUserRole, loggedUserId, fromDate, toDate);
                        setAppliedDates({ start: fromDate, end: toDate });
                      }}
                    >
                      Filter
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        const start = getFirstOfMonthStr();
                        const end = getTodayStr();
                        setFromDate(start);
                        setToDate(end);
                        fetchCampaigns(loggedUserRole, loggedUserId, start, end);
                      }}
                    >
                      Clear
                    </Button>
                  </div>
                </div>

                <div className="pb-4">
                  <div style={{ overflowX: 'auto' }} className="broad-scrollbar">
                    <DataTable
                      tableId="campaigns.live"
                      columns={columns}
                      data={filteredCampaigns}
                      pageSize={10}
                      searchPlaceholder="Search by campaign name…"
                      emptyTitle="No campaigns found"
                      disableExport
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        <NumbersViewerModal
          show={numbersViewer.show}
          onHide={() => setNumbersViewer({ show: false, id: null, name: null, total: 0 })}
          campaignId={numbersViewer.id}
          campaignName={numbersViewer.name}
          expectedTotal={numbersViewer.total}
        />
        <style>{`
  .broad-scrollbar::-webkit-scrollbar { height: 8px; }
  .live-dot {
    width: 10px; height: 10px; border-radius: 50%;
    background: #22c55e; display: inline-block;
    box-shadow: 0 0 0 rgba(34,197,94, 0.6);
    animation: livePulse 1.6s infinite;
  }
  @keyframes livePulse {
    0%   { box-shadow: 0 0 0 0 rgba(34,197,94, 0.5); }
    70%  { box-shadow: 0 0 0 8px rgba(34,197,94, 0); }
    100% { box-shadow: 0 0 0 0 rgba(34,197,94, 0); }
  }
  .kpi-card {
    border-radius: 14px; background: #fff;
    box-shadow: 0 2px 10px rgba(16,24,40,0.06);
    transition: transform .15s ease, box-shadow .15s ease;
  }
  .kpi-card:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(16,24,40,0.10); }
  .kpi-icon { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 20px; flex: 0 0 auto; }
  .kpi-icon--blue   { background: #e8ecff; color: #3b47d6; }
  .kpi-icon--green  { background: #e3f6f0; color: #11707d; }
  .kpi-icon--orange { background: #fdf2df; color: #d39e00; }
  .kpi-label { font-size: 12px; color: #6b7280; font-weight: 600; text-transform: uppercase; letter-spacing: .03em; }
  .kpi-value { font-size: 26px; font-weight: 700; line-height: 1.1; }
  .kpi-substat { font-size: 12px; color: #6b7280; }
  .kpi-substat .dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; margin-right: 4px; }
  .dot--run { background: #22c55e; }
  .dot--pause { background: #035fa5; }
  .dot--wait { background: #cf5b03; }
  .live-refresh-btn .pi-spin { animation: pispin 0.8s linear infinite; }
  @keyframes pispin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
  .metric-section { border: 1px solid hsl(var(--border) / 0.6); border-radius: 12px; padding: 10px 12px 12px; margin-bottom: 12px; background: hsl(var(--card)); }
  .metric-section:last-child { margin-bottom: 0; }
  .metric-section__title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: hsl(var(--muted-foreground)); margin-bottom: 8px; }
  /* Subtle rectangular metric tile — square-ish corners, quiet neutral
     surface, only the icon well carries the semantic tint. Reads as a
     data chip rather than a colored pill. */
  .metric-pill {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 5px 10px 5px 5px;
    border-radius: 8px;
    font-weight: 500; font-size: 12.5px;
    background: hsl(var(--card));
    border: 1px solid hsl(var(--border) / 0.5);
    color: hsl(var(--foreground));
    line-height: 1.2;
  }
  .metric-pill svg,
  .metric-pill > :first-child {
    display: inline-flex; align-items: center; justify-content: center;
    width: 22px; height: 22px; border-radius: 6px; flex: 0 0 22px;
    font-size: 13px;
  }
  .metric-pill--blue > :first-child   { background: rgba(37,99,235,0.12); color: rgb(37,99,235); }
  .metric-pill--teal > :first-child   { background: rgba(20,184,166,0.12); color: rgb(15,118,110); }
  .metric-pill--green > :first-child  { background: rgba(16,185,129,0.12); color: rgb(4,120,87); }
  .metric-pill--purple > :first-child { background: rgba(139,92,246,0.12); color: rgb(109,40,217); }
  .metric-pill--red > :first-child    { background: rgba(244,63,94,0.12); color: rgb(190,18,60); }
  .metric-pill--orange > :first-child { background: rgba(249,115,22,0.12); color: rgb(194,65,12); }
  .metric-pill--violet > :first-child { background: rgba(147,51,234,0.12); color: rgb(107,33,168); }
  /* Small pulsing green dot for actively-running statuses in the pill. */
  .live-status-dot {
    width: 6px; height: 6px; border-radius: 50%;
    background: currentColor;
    display: inline-block;
    box-shadow: 0 0 0 rgba(0,0,0,0);
    animation: liveStatusPulse 1.6s infinite;
  }
  @keyframes liveStatusPulse {
    0%   { box-shadow: 0 0 0 0 currentColor; opacity: 1; }
    70%  { box-shadow: 0 0 0 4px transparent; opacity: 0.65; }
    100% { box-shadow: 0 0 0 0 transparent; opacity: 1; }
  }
`}</style>
      </div>
    </FillPage>
  );
};

export default CampaignsList;
